import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Button } from 'react-bootstrap';
import Select from 'react-select';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import apiClient from "../../utils/api";
import PageHeader1 from '../../components/common/PageHeader1';

const PARKING_TYPES = [
    { value: 'Normal', label: 'Normal' },
    { value: 'VIP', label: 'VIP' },
    { value: 'Complementary', label: 'Complementary' }
];

const STATUS_OPTIONS = [
    { value: 'parked', label: 'Parked' },
    { value: 'requested', label: 'Requested' },
    { value: 'dispatched', label: 'Dispatched' },
    { value: 'delivered', label: 'Delivered' }
];

function ParkingEdit() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(false);
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [colors, setColors] = useState([]);    
    const [exitLocations, setExitLocations] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [damageMarks, setDamageMarks] = useState([]);
    const [hasDamage, setHasDamage] = useState(false);
    const [damagedCarImage, setDamagedCarImage] = useState(null);
    
    const [formData, setFormData] = useState({
        ticket_number: '',
        mobile_number: '',
        plate_number: '',
        brand_id: '',
        model_id: '',
        color_id: '',
        images: [],
        existing_images: [], 
        notes: '',
        status: 'active',
        exit_location_id: '',
        driver_id: ''
    });

    useEffect(() => {
        fetchDropdownData();
        fetchParkingDetails();
    }, [id]);    // Fetch Valet Locations when parking type changes or component mounts
    
    useEffect(() => {
        if (formData.parking_type) {
            fetchExitLocations(formData.parking_type, true); // Pass true to keep existing location
            // If there's already an Valet Location selected, fetch its drivers
            if (formData.exit_location_id) {
                fetchDrivers(formData.exit_location_id);
            }
        }
    }, [formData.parking_type]);

    const fetchDrivers = async (locationId) => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/drivers/by-location/${locationId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const driversData = response.data.data;
            setDrivers(driversData);
            
            // Auto-select driver logic - but only if no driver is currently selected
            if (!formData.driver_id) {
                if (driversData.length === 1) {
                    // If only one driver, auto-select it
                    setFormData(prev => ({
                        ...prev,
                        driver_id: driversData[0].id.toString()
                    }));
                } else if (driversData.length > 1 && user) {
                    // If multiple drivers, try to auto-select the logged-in user if they're in the list
                    const currentUserDriver = driversData.find(driver => 
                        driver.user_id === user.id || 
                        driver.email === user.email ||
                        driver.id === user.id
                    );
                    
                    if (currentUserDriver) {
                        setFormData(prev => ({
                            ...prev,
                            driver_id: currentUserDriver.id.toString()
                        }));
                    }
                }
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to load drivers');
            setDrivers([]);
        }
    };

    const fetchDropdownData = async () => {
        try {
            const token = localStorage.getItem('auth_token');            
            const [brandsRes, colorsRes] = await Promise.all([
                apiClient.get('accounts/brands', {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                apiClient.get('accounts/colors', {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setBrands(brandsRes.data.data);
            setColors(colorsRes.data.data);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error loading dropdown data');
        }
    };

    const fetchParkingDetails = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/parkings/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const parking = response.data.data;
              if (parking.brand_id) {
                fetchModels(parking.brand_id);
            }

            setHasDamage(parking.has_damage || false);
            // Parse damage_marks if it's a string, or use empty array as fallback
            const marks = parking.damage_marks ? 
                (typeof parking.damage_marks === 'string' ? 
                    JSON.parse(parking.damage_marks) : 
                    parking.damage_marks) : 
                [];
            setDamageMarks(marks);
            
            // If there's an existing damage image, we'll need to handle it
            if (parking.damage_image_url) {
                // Store the URL for display purposes, but not the actual blob for editing
                setDamagedCarImage(parking.damage_image_url);
            }
            
            // Convert parking type to string value if it's not already
            const parkingType = parking.parking_type || 'Normal';
            
            // Set form data
            setFormData({
                ...formData,
                ticket_number: parking.ticket_number,
                mobile_number: parking.mobile_number,
                plate_number: parking.plate_number,
                brand_id: parking.brand_id,
                model_id: parking.model_id,
                color_id: parking.color_id,
                notes: parking.notes,
                status: parking.status,
                parking_type: parking.parking_type || 'Normal',
                exit_location_id: parking.exit_location_id,
                driver_id: parking.driver_id,
                existing_images: parking.images || []
            });

            // Fetch related data
            fetchExitLocations(parkingType, true); // true means keep existing location
            if (parking.brand_id) {
                fetchModels(parking.brand_id.toString());
            }

            // If there's an Valet Location, fetch the drivers for it
            if (parking.exit_location_id) {
                fetchDrivers(parking.exit_location_id);
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error loading parking details');
        }
    };

    const fetchModels = async (brandId) => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get('accounts/models', {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Filter models by brand and active status
            const allModels = response.data.data.filter(model => 
                model.brand_id === parseInt(brandId) && 
                model.is_active === 1
            );
            setModels(allModels);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error loading car models');
            setModels([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, files } = e.target;

        if (name === 'mobile_number') {
            // Only allow digits and limit to 8 characters
            const sanitizedValue = value.replace(/\D/g, '').slice(0, 8);
            setFormData(prev => ({
                ...prev,
                [name]: sanitizedValue
            }));
            return;
        }
        
        if (type === 'file') {
            setFormData(prev => ({
                ...prev,
                [name]: Array.from(files)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        if (name === 'brand_id' && value) {
            fetchModels(value);
            setFormData(prev => ({
                ...prev,
                model_id: ''
            }));
        }       
        
        // When parking type changes, fetch corresponding Valet Locations
        if (name === 'parking_type' && value) {
            fetchExitLocations(value);
            setFormData(prev => ({
                ...prev,
                exit_location_id: '',
                driver_id: ''
            }));
        }

        // When Valet Location changes, fetch corresponding drivers
        if (name === 'exit_location_id' && value) {
            fetchDrivers(value);
        }
    };    
    
    const fetchExitLocations = async (parkingType, shouldKeepExistingLocation = false) => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/parkings/active-exit-locations/${parkingType}`,{
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const locations = response.data.data;
            setExitLocations(locations);
            
            if (shouldKeepExistingLocation && formData.exit_location_id) {
                // If we should keep existing selection and it exists in new locations, keep it
                const locationExists = locations.some(loc => loc.id.toString() === formData.exit_location_id.toString());
                if (locationExists) {
                    fetchDrivers(formData.exit_location_id);
                    return;
                }
            }
            
            // Auto-select if there's only one location
            if (locations.length === 1) {
                const locationId = locations[0].id.toString();
                setFormData(prev => ({
                    ...prev,
                    exit_location_id: locationId
                }));
                fetchDrivers(locationId);
            }
        } catch (error) {
            toast.error('Failed to load Valet Locations');
            console.error('Error fetching Valet Locations:', error);
        }
    };

    const handleImageClick = (event) => {
        const isTouch = event.type === 'touchend';
        const touch = isTouch ? event.changedTouches?.[0] : event;
        if (!touch) return;

        const rect = event.target.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        const clickPosition = { x: Math.round(x), y: Math.round(y) };

        setDamageMarks(prev => {
            const newMarks = [...prev, clickPosition];
            setHasDamage(true);
            
            // Create and save the edited image
            const canvas = document.createElement('canvas');
            const img = event.target;
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            
            // Draw original image
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            
            // Draw all damage marks
            [...prev, clickPosition].forEach(mark => {
                ctx.beginPath();
                ctx.arc(mark.x, mark.y, 5, 0, 2 * Math.PI);
                ctx.fillStyle = 'black';
                ctx.fill();
            });

            // Convert to blob and store
            canvas.toBlob((blob) => {
                setDamagedCarImage(blob);
            }, 'image/png');

            return newMarks;
        });
    };

    const handleRemoveLastMark = () => {
        setDamageMarks(prev => {
            const updated = [...prev];
            updated.pop();
            if (updated.length === 0) {
                setHasDamage(false);
                setDamagedCarImage(null); // Clear damage image when no marks
            }
            return updated;
        });
    };

    const handleResetAllMarks = () => {
        setDamageMarks([]);
        setHasDamage(false);
        setDamagedCarImage(null);
    };

    const customStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? '#a7824f' : '#ced4da',
            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(167, 130, 79, 0.25)' : '',
            '&:hover': {
                borderColor: '#a7824f',
            },
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#a7824f' : state.isFocused ? 'rgba(167, 130, 79, 0.1)' : '',
            color: state.isSelected ? 'white' : 'black',
            '&:active': {
                backgroundColor: '#a7824f',
            },
        }),
    };

    const handleImageDelete = async (imageIndex) => {
        try {
            const token = localStorage.getItem('auth_token');
            await apiClient.delete(`accounts/parkings/${id}/images/${imageIndex}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFormData(prev => ({
                ...prev,
                existing_images: prev.existing_images.filter((_, idx) => idx !== imageIndex)
            }));
            toast.success('Image deleted successfully');
        } catch (error) {
            console.error('Error deleting image:', error);
            toast.error('Failed to delete image');
        }
    };

    const handleDamageImageDelete = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            await apiClient.delete(`accounts/parkings/${id}/damage-image`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDamagedCarImage(null);
            setDamageMarks([]);
            setHasDamage(false);
            toast.success('Damage image deleted successfully');
        } catch (error) {
            console.error('Error deleting damage image:', error);
            toast.error('Failed to delete damage image');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate mobile number format if provided
        if (formData.mobile_number && formData.mobile_number.length > 0 && !/^\d{8}$/.test(formData.mobile_number)) {
            toast.error('If provided, mobile number must be exactly 8 digits');
            return;
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem('auth_token');
            const data = new FormData();

            // Required fields
            data.append('ticket_number', formData.ticket_number);
            data.append('plate_number', formData.plate_number);
            
            // Optional fields - only append if they have values
            if (formData.mobile_number) {
                data.append('mobile_number', formData.mobile_number);
            }
            if (formData.brand_id) {
                data.append('brand_id', formData.brand_id);
            }
            if (formData.model_id) {
                data.append('model_id', formData.model_id);
            }
            if (formData.color_id) {
                data.append('color_id', formData.color_id);
            }
            if (formData.notes) {
                data.append('notes', formData.notes);
            }            
            if (formData.exit_location_id) {
                data.append('exit_location_id', formData.exit_location_id);
            }
            if (formData.driver_id) {
                data.append('driver_id', formData.driver_id);
            }

            // Handle damage marks
            data.append('has_damage', hasDamage ? 1 : 0);
            data.append('damage_marks', JSON.stringify(damageMarks));
            
            // Handle damage image if it's a new blob (not just a URL)
            if (damagedCarImage && damagedCarImage instanceof Blob) {
                data.append('damage_image', damagedCarImage, 'damage_image.png');
            }
            
            data.append('parking_type', formData.parking_type);

            // Handle image uploads
            if (formData.images && formData.images.length > 0) {
                formData.images.forEach(image => {
                    data.append('images[]', image);
                });
            }

            data.append('_method', 'PUT');

            const response = await apiClient.post(`accounts/parkings/${id}`, data, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            toast.success('Parking record updated successfully');
            // Redirect based on whether mobile number was provided
            if (!formData.mobile_number) {
                // If no mobile number, go to print options
                navigate(`/accounts/parking-print-options/${id}`);
            } else {
                // If mobile number was provided, go directly to parking list
                navigate('/accounts/parking-list');
            }
        } catch (error) {
            console.error('Error:', error);
            const errorMessage = error.response?.data?.message || 'Error updating parking record';
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="body d-flex py-3">
            <div className="container-xxl">
                <PageHeader1 pagetitle="Edit Parking" />
                <Card>
                    <Card.Body>
                        <Form onSubmit={handleSubmit}>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Ticket Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="ticket_number"
                                            value={formData.ticket_number}
                                            readOnly
                                        />
                                    </Form.Group>
                                </Col>
                                
                                <Col md={6}>                                    
                                    <Form.Group>
                                        <Form.Label>Mobile Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="mobile_number"
                                            value={formData.mobile_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter 8 digits"
                                            maxLength="8"
                                        />
                                        <Form.Text className="text-muted">
                                            Enter exactly 8 digits (without country code)
                                        </Form.Text>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Plate Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="plate_number"
                                            value={formData.plate_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter plate number"
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>                                            <Form.Label>Car Brand</Form.Label>
                                            <Select
                                                key={`brand-${brands.length}-${formData.brand_id}`}
                                                name="brand_id"
                                                value={brands.find(brand => brand.id === parseInt(formData.brand_id)) || null}
                                                onChange={(selectedOption) => {
                                                    handleInputChange({
                                                        target: {
                                                            name: 'brand_id',
                                                            value: selectedOption ? selectedOption.id.toString() : ''
                                                        }
                                                    });
                                                }}
                                                options={brands}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id.toString()}
                                                isClearable
                                                isSearchable
                                                placeholder="Search or select brand"
                                                noOptionsMessage={() => brands.length === 0 ? "Loading brands..." : "No brands found"}
                                                styles={customStyles}
                                                isLoading={brands.length === 0}
                                            />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>                                            <Form.Label>Car Model</Form.Label>
                                            <Select
                                                key={`model-${models.length}-${formData.model_id}`}
                                                name="model_id"
                                                value={models.find(model => model.id === parseInt(formData.model_id)) || null}
                                                onChange={(selectedOption) => {
                                                    handleInputChange({
                                                        target: {
                                                            name: 'model_id',
                                                            value: selectedOption ? selectedOption.id.toString() : ''
                                                        }
                                                    });
                                                }}
                                                options={models}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id.toString()}
                                                isClearable
                                                isSearchable
                                                placeholder="Search or select model"
                                                noOptionsMessage={() => !formData.brand_id ? "Select a brand first" : models.length === 0 ? "Loading models..." : "No models found"}
                                                styles={customStyles}
                                                isDisabled={!formData.brand_id}
                                                isLoading={formData.brand_id && models.length === 0}
                                            />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>                                            <Form.Label>Car Color</Form.Label>
                                            <Select
                                                key={`color-${colors.length}-${formData.color_id}`}
                                                name="color_id"
                                                value={colors.find(color => color.id === parseInt(formData.color_id)) || null}
                                                onChange={(selectedOption) => {
                                                    handleInputChange({
                                                        target: {
                                                            name: 'color_id',
                                                            value: selectedOption ? selectedOption.id.toString() : ''
                                                        }
                                                    });
                                                }}
                                                options={colors}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id.toString()}
                                                isClearable
                                                isSearchable
                                                placeholder="Search or select color"
                                                noOptionsMessage={() => colors.length === 0 ? "Loading colors..." : "No colors found"}
                                                styles={customStyles}
                                                isLoading={colors.length === 0}
                                            />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Upload New Images</Form.Label>
                                        <Form.Control
                                            type="file"
                                            name="images"
                                            onChange={handleInputChange}
                                            multiple
                                            accept="image/jpeg,image/png"
                                        />
                                        <Form.Text className="text-muted">
                                            You can select multiple new images (JPG/PNG)
                                        </Form.Text>
                                    </Form.Group>
                                </Col>

                                {formData.existing_images.length > 0 && (
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Existing Images</Form.Label>
                                            <div className="row g-2">
                                                {formData.existing_images.map((image, index) => (
                                                    <div key={index} className="col-auto">
                                                        <div className="position-relative">                                                        <a 
                                                                href={`${process.env.REACT_APP_STORAGE_URL}${image}`} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                            >
                                                                <img 
                                                                    src={`${process.env.REACT_APP_STORAGE_URL}${image}`} 
                                                                    alt={`Parking ${index + 1}`}
                                                                    className="img-thumbnail"
                                                                    style={{
                                                                        height: "100px",
                                                                        cursor: "pointer"
                                                                    }}
                                                                />
                                                            </a>
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                className="position-absolute top-0 end-0"
                                                                onClick={() => handleImageDelete(index)}
                                                            >
                                                                ×
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </Form.Group>
                                    </Col>
                                )}

                                {/* Add damage marking section */}
                                <Col md={12}>
                                    <div className="mb-4">
                                        <h6>Mark Damages</h6>
                                        <div className="mb-3 mt-3">
                                            <Form.Check
                                                type="checkbox"
                                                label="There is no damage."
                                                checked={!hasDamage}
                                                onChange={() => {
                                                    setHasDamage(false);
                                                    setDamageMarks([]);
                                                    setDamagedCarImage(null);
                                                }}
                                                className="custom-outline-red"
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                label="There are damages."
                                                checked={hasDamage}
                                                onChange={() => setHasDamage(true)}
                                                className="custom-outline-red"
                                            />
                                        </div>
                                        <div style={{ display: 'flex', position: 'relative' }}>
                                            <img
                                                src="/carimage2.png"
                                                alt="Car Diagram"
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '400px',
                                                    borderRadius: '10px',
                                                    border: '1px solid #ddd',
                                                    cursor: 'crosshair'
                                                }}
                                                onClick={handleImageClick}
                                                onTouchEnd={handleImageClick}
                                            />
                                            {damageMarks.map((dot, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        position: 'absolute',
                                                        top: dot.y - 5 + 'px',
                                                        left: dot.x - 5 + 'px',
                                                        width: '10px',
                                                        height: '10px',
                                                        backgroundColor: 'black',
                                                        borderRadius: '50%'
                                                    }}
                                                ></div>
                                            ))}
                                        </div>

                                        <div className="mt-3 d-flex justify-content-center gap-2">
                                            <Button variant="outline-primary" size="sm" onClick={handleRemoveLastMark}>
                                                Remove Last Mark
                                            </Button>
                                            <Button variant="outline-danger" size="sm" onClick={handleResetAllMarks}>
                                                Reset All Marks
                                            </Button>
                                        </div>
                                        
                                        {/* Show existing damage image if available */}
                                        {damagedCarImage && typeof damagedCarImage === 'string' && (
                                            <div className="mt-3">
                                                <h6>Current Damage Image</h6>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img 
                                                        src={damagedCarImage} 
                                                        alt="Current Damage" 
                                                        style={{
                                                            maxWidth: '200px',
                                                            maxHeight: '150px',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '5px'
                                                        }}
                                                    />
                                                    <Button 
                                                        variant="outline-danger" 
                                                        size="sm" 
                                                        onClick={handleDamageImageDelete}
                                                    >
                                                        Delete Damage Image
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>Notes</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="notes"
                                            value={formData.notes}
                                            onChange={handleInputChange}
                                            placeholder="Enter any additional notes or damage description"
                                        />
                                    </Form.Group>
                                </Col>                                <Col md={6}>
                                    <Form.Group>                                            <Form.Label>Parking Type</Form.Label>
                                            <Select
                                                key={`parking-type-${formData.parking_type}`}
                                                name="parking_type"
                                                value={PARKING_TYPES.find(type => type.value === formData.parking_type) || null}
                                                onChange={(selectedOption) => {
                                                    handleInputChange({
                                                        target: {
                                                            name: 'parking_type',
                                                            value: selectedOption.value
                                                        }
                                                    });
                                                }}
                                                options={PARKING_TYPES}
                                                isClearable={false}
                                                isSearchable
                                                placeholder="Select parking type"
                                                styles={customStyles}
                                                required
                                            />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Valet Location</Form.Label>
                                        <Select
                                            key={`exit-location-${exitLocations.length}-${formData.exit_location_id}`}
                                            name="exit_location_id"
                                            value={exitLocations.find(location => location.id === parseInt(formData.exit_location_id)) || null}
                                            onChange={(selectedOption) => {
                                                handleInputChange({
                                                    target: {
                                                        name: 'exit_location_id',
                                                        value: selectedOption ? selectedOption.id.toString() : ''
                                                    }
                                                });
                                            }}
                                            options={exitLocations}
                                            getOptionLabel={(option) => option.name}
                                            getOptionValue={(option) => option.id.toString()}
                                            isClearable={false}
                                            isSearchable
                                            placeholder="Search or select valet location"
                                            noOptionsMessage={() => exitLocations.length === 0 ? "Loading locations..." : "No locations found"}
                                            styles={customStyles}
                                            isLoading={exitLocations.length === 0}
                                            required
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>                                            
                                        <Form.Label>Driver</Form.Label>
                                            <Select
                                                key={`driver-${drivers.length}-${formData.driver_id}`}
                                                name="driver_id"
                                                value={drivers.find(driver => driver.id === parseInt(formData.driver_id)) || null}
                                                onChange={(selectedOption) => {
                                                    handleInputChange({
                                                        target: {
                                                            name: 'driver_id',
                                                            value: selectedOption ? selectedOption.id.toString() : ''
                                                        }
                                                    });
                                                }}
                                                options={drivers}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id.toString()}
                                                isClearable={false}
                                                isSearchable
                                                placeholder="Search or select driver"
                                                noOptionsMessage={() => !formData.exit_location_id ? "Select a location first" : drivers.length === 0 ? "Loading drivers..." : "No drivers found"}
                                                styles={customStyles}
                                                isDisabled={!formData.exit_location_id}
                                                isLoading={formData.exit_location_id && drivers.length === 0}
                                                required
                                            />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Status</Form.Label>
                                        <Select
                                            key={`status-${formData.status}`}
                                            name="status"
                                            value={STATUS_OPTIONS.find(option => option.value === formData.status) || null}
                                            onChange={(selectedOption) => {
                                                handleInputChange({
                                                    target: {
                                                        name: 'status',
                                                        value: selectedOption.value
                                                    }
                                                });
                                            }}
                                            options={STATUS_OPTIONS}
                                            isClearable={false}
                                            isSearchable
                                            placeholder="Select status"
                                            styles={customStyles}
                                            required
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Button 
                                        type="submit" 
                                        variant="primary"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Updating...' : 'Update Parking Record'}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
}

export default ParkingEdit;